import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { useGetStudentByIdQuery } from "../api/studentApi";
import { API_CONFIG } from "../../../../core/config";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

interface InfoItemStyles {
  infoItem: React.CSSProperties;
  infoLabel: React.CSSProperties;
  infoValue: React.CSSProperties;
  infoIcon: React.CSSProperties;
}

interface InfoItemProps {
  label: string;
  value: string;
  valueClassName?: string;
  icon?: string;
  styles: InfoItemStyles;
}

const GenerateStudentID = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const {
    data: studentResponse,
    isLoading,
    error,
  } = useGetStudentByIdQuery({ id: id || "" });
  const student = studentResponse?.data?.student;
  const navigate = useNavigate();
  const registeredDate = new Date(student?.created_at || '');
  const expiryDate = new Date(registeredDate);
  expiryDate.setMonth(expiryDate.getMonth() + 12);
  const formattedExpiryDate = expiryDate.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  const handleBack = () => {
    navigate(-1);
  };
  
  const styles = {
    gradientBg: {
      background: "linear-gradient(135deg, var(--bs-primary) 0%, #2c80ff 100%)",
    },
    gradientText: {
      color: "var(--bs-primary)",
      fontWeight: "bold",
    },
    watermark: {
      position: "absolute" as const,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "200px",
      height: "200px",
      opacity: 0.1,
      pointerEvents: "none" as const,
    },
    photoOverlay: {
      position: "absolute" as const,
      bottom: 0,
      left: 0,
      right: 0,
      height: "30%",
    },
    infoGrid: {
      borderLeft: "3px solid var(--bs-primary)",
      paddingLeft: "1rem",
    },
    verifiedBadge: {
      padding: "0.5rem 1rem",
      background: "rgba(255, 255, 255, 0.1)",
      borderRadius: "20px",
    },
    dashedBorder: {
      border: "2px dashed rgba(13, 110, 253, 0.3)",
    },
    letterSpacing1: {
      letterSpacing: "1px",
    },
    letterSpacing2: {
      letterSpacing: "2px",
    },
  };

  const infoStyles: InfoItemStyles = {
    infoItem: {
      background: "rgba(var(--bs-primary-rgb), 0.03)",
      borderRadius: "8px",
      transition: "all 0.3s ease",
    },
    infoLabel: {
      fontSize: "0.75rem",
      textTransform: "uppercase" as const,
      color: "#6c757d",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    infoValue: {
      textWrap: "nowrap",
      fontSize: "0.9rem",
      marginBottom: "0.5rem",
      fontWeight: "600",
      color: "var(--bs-primary)",
      display: "block",
    },
    infoIcon: {
      fontSize: "1rem",
      color: "var(--bs-primary)",
      opacity: 0.8,
    },
  };

  const cardRef = React.useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!cardRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      const cardElement = cardRef.current;
      const canvas = await html2canvas(cardElement, {
        scale: 4,
        useCORS: true,
        logging: false,
        allowTaint: true,
        imageTimeout: 0,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const yPosition = (pageHeight - imgHeight) / 2;

      pdf.addImage(
        imgData,
        "PNG",
        0,
        Math.max(yPosition, 0),
        imgWidth,
        imgHeight
      );

      pdf.save(`student-id-${student?.profile?.studentid || "unknown"}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error loading student data</div>;
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Generate Student ID</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.studentList}>Students</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Generate Student ID
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="mb-2 me-2">
              <button
                onClick={handleBack}
                className="btn btn-outline-primary d-flex align-items-center"
              >
                <i className="ti ti-arrow-left me-2" />
                Back
              </button>
            </div>
            <div className="mb-2">
              <button
                onClick={handleDownload}
                className="btn btn-primary d-flex align-items-center"
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="ti ti-square-rounded-plus me-2" />
                    Download
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="row justify-content-center" ref={cardRef}>
          <div className="col-xl-4 col-md-6">
            {/* Main Card */}
            <div className="card shadow-xl border-0 rounded-4 overflow-hidden position-relative">
              {/* Header Section */}
              <div className="text-white p-2" style={styles.gradientBg}>
                <div className="text-center position-relative">
                  <div style={styles.watermark}></div>
                  <ImageWithBasePath
                    src="assets/img/logo.png"
                    alt="School Logo"
                    className="img-fluid"
                    width={150}
                    height={35}
                  />
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <span className="badge bg-white text-primary px-3 py-2 rounded-pill">
                      STUDENT
                    </span>
                    <span className="fw-bold" style={styles.letterSpacing2}>
                      IDENTITY CARD
                    </span>
                  </div>
                </div>
              </div>

              {/* Body Section */}
              <div className="card-body p-2 pt-4">
                <div className="row g-4">
                  {/* Left Column - Photo */}
                  <div className="col-4">
                    <div className="position-relative">
                      <div
                        className="mb-3 border-3 border-primary rounded-4 shadow-sm position-relative"
                        style={{
                          width: "100%",
                          paddingBottom: "130%",
                          overflow: "hidden",
                        }}
                      >
                        {student?.docs?.photo ? (
                          <img
                            src={`${API_CONFIG.BASE_URL}students/get-thumbnail/${student.docs.photo}`}
                            alt="Student Photo"
                            className="position-absolute w-100 h-100 object-fit-cover"
                            onError={(
                              e: React.SyntheticEvent<HTMLImageElement>
                            ) => {
                              const target = e.target as HTMLImageElement;
                              target.src =
                                student.profile.gender?.toLowerCase() ===
                                "female"
                                  ? "/assets/img/profiles/default-female.png"
                                  : "/assets/img/profiles/default-male.png";
                            }}
                          />
                        ) : (
                          <ImageWithBasePath
                            src={
                              student?.profile?.gender?.toLowerCase() ===
                              "female"
                                ? "assets/img/profiles/default-female.png"
                                : "assets/img/profiles/default-male.png"
                            }
                            alt="Student Photo"
                            className="position-absolute w-100 h-100 object-fit-cover"
                          />
                        )}
                      </div>
                      <div style={styles.photoOverlay}></div>
                    </div>
                  </div>

                  {/* Right Column - Details */}
                  <div className="col-8">
                    <h3 className="mb-2" style={styles.gradientText}>
                      {student?.first_name} {student?.last_name}
                    </h3>
                    <div className="d-flex align-items-center mb-4">
                      <span
                        className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill"
                        style={{
                          fontSize: "0.5rem",
                        }}
                      >
                        Student ID: {student?.profile?.studentid}
                      </span>
                    </div>

                    <div style={styles.infoGrid}>
                      <InfoItem
                        label="Course"
                        value={student?.course?.name || "N/A"}
                        valueClassName="text-primary"
                        icon="ti-book"
                        styles={infoStyles}
                      />
                      <InfoItem
                        label="Batch"
                        value={student?.batch?.name || "N/A"}
                        valueClassName="text-primary"
                        icon="ti-clock"
                        styles={infoStyles}
                      />
                      <InfoItem
                        label="Contact"
                        value={student?.profile?.phone?.toString() || "N/A"}
                        valueClassName="text-primary"
                        icon="ti-phone"
                        styles={infoStyles}
                      />
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div
                  className="mt-4 bg-light p-3 rounded-4"
                  style={styles.dashedBorder}
                >
                  <div className="d-flex align-items-center mb-2">
                    <i className="ti ti-map-pin me-2 text-primary"></i>
                    <small
                      className="text-uppercase fw-bold text-primary"
                      style={styles.letterSpacing1}
                    >
                      Address
                    </small>
                  </div>
                  <p className="mb-0 text-dark">
                    {student?.address?.street}, {student?.address?.city},
                    <br />
                    {student?.address?.state} -{" "}
                    {student?.address?.pincode?.toString()}
                  </p>
                </div>
              </div>

              {/* Footer Section */}
              <div className="py-3 px-4 text-white" style={styles.gradientBg}>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <i className="ti ti-calendar me-2"></i>
                    <span className="fw-semibold">Valid Until:</span>{" "}
                    <span className="badge bg-white text-primary ms-2 rounded-pill">
                      {formattedExpiryDate}
                    </span>
                  </div>
                  <div
                    className="d-flex align-items-center"
                    style={styles.verifiedBadge}
                  >
                    <i className="ti ti-shield-check me-2"></i>
                    <span className="fw-semibold">Verified</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value, icon, styles }) => (
  <div style={styles.infoItem}>
    <div style={styles.infoLabel}>
      {icon && <i className={`ti ${icon}`} style={styles.infoIcon}></i>}
      {label}
    </div>
    <span style={styles.infoValue}>{value}</span>
  </div>
);

export default GenerateStudentID;
